/**
 * Report layout extraction + hydration pipeline (Story 37.1).
 *
 * Pure helpers that read the per-report fields embedded in a `layoutJson` blob
 * (section labels, empty sections, custom section order, zoom) and assemble the
 * editor's initial state when LOADING a saved report. Extracted from the route
 * component so the persistence round-trip is testable as a pure seam (no DOM /
 * no route render harness) — see reportLayout.test.ts.
 *
 * The template path and the saved-report load path BOTH funnel through these
 * helpers, guaranteeing the two stay symmetric: anything a template can express,
 * a saved report restores identically.
 *
 * Every helper is defensive against non-object / corrupt input (returns empty
 * defaults), so a `null` from `safeParseLayout` flows through safely without
 * wiping sibling state or throwing (AC #4).
 */

import {
  migrateLayoutToPages,
  safeParseLayout,
} from "@provenleap/types/report-migration";
import type { ReportPage } from "@provenleap/types/report-rendering";
import { ZOOM_MIN, ZOOM_MAX } from "../hooks/useCanvasZoom";

/** Default editor zoom when a layout has no valid saved `zoomLevel`. */
export const DEFAULT_ZOOM_LEVEL = 80;

/** Reads the per-category section-label overrides saved in a report layout. */
export function extractSectionLabels(
  parsedLayout: unknown,
): Record<string, string> {
  if (!parsedLayout || typeof parsedLayout !== "object") return {};
  const raw = (parsedLayout as Record<string, unknown>).sectionLabels;
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "string" && v.trim()) out[k] = v;
  }
  return out;
}

/** Sections that exist independently of pages (empty sections), from layoutJson. */
export function extractActiveSections(parsedLayout: unknown): string[] {
  if (!parsedLayout || typeof parsedLayout !== "object") return [];
  const raw = (parsedLayout as Record<string, unknown>).activeSections;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === "string");
}

/** Custom display order of sections (category ids), from layoutJson. */
export function extractSectionOrder(parsedLayout: unknown): string[] {
  if (!parsedLayout || typeof parsedLayout !== "object") return [];
  const raw = (parsedLayout as Record<string, unknown>).sectionOrder;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === "string");
}

/** True when the parsed layout carries a valid (in-range) saved zoom level. */
export function hasSavedZoomLevel(parsedLayout: unknown): boolean {
  if (!parsedLayout || typeof parsedLayout !== "object") return false;
  const raw = (parsedLayout as Record<string, unknown>).zoomLevel;
  return typeof raw === "number" && raw >= ZOOM_MIN && raw <= ZOOM_MAX;
}

/**
 * Reads a valid editor zoom level from a parsed layout, falling back to
 * {@link DEFAULT_ZOOM_LEVEL} when absent or out of the [ZOOM_MIN, ZOOM_MAX]
 * range. Mirrors the validation the route applies for the template path.
 */
export function extractZoomLevel(parsedLayout: unknown): number {
  if (!hasSavedZoomLevel(parsedLayout)) return DEFAULT_ZOOM_LEVEL;
  return (parsedLayout as Record<string, unknown>).zoomLevel as number;
}

/** Saved report shape consumed by {@link buildReportHydration}. */
export interface SavedReportInput {
  layoutJson: string | null;
  comparisonStart: string | null;
  comparisonEnd: string | null;
  /** Authoritative top-level format column (correction C3). */
  format: "document" | "slide-deck" | null;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  name?: string | null;
}

/** Fully-resolved editor state produced from a saved report. */
export interface ReportHydrationState {
  pages: ReportPage[];
  sectionLabels: Record<string, string>;
  activeSections: string[];
  sectionOrder: string[];
  zoomLevel: number;
  /** True when `zoomLevel` came from a valid saved value (vs the default). */
  hasSavedZoom: boolean;
  format: "document" | "slide-deck";
  name: string | null;
  /** Date-range hydration payload for `useDateRange.hydrate`, or null if absent. */
  dateRange: {
    start: string;
    end: string;
    comparisonStart: string | null;
    comparisonEnd: string | null;
  } | null;
  /** True when the raw layoutJson failed to parse (degraded to defaults). */
  layoutCorrupt: boolean;
}

/**
 * Pure round-trip resolver (Story 37.1): turns a saved report row into the exact
 * editor state the route hydrates. This is the seam the round-trip + corruption
 * tests exercise without a DOM.
 *
 * Contract:
 * - `layoutJson` is parsed defensively. On corruption every layout-derived field
 *   degrades to its empty default and `layoutCorrupt` is true — section state is
 *   never wiped from a sibling source because there is none to wipe here.
 * - `format` uses the top-level column (C3); `layoutJson.format` is ignored.
 * - the date-range payload is only produced when BOTH primary ends exist; the
 *   comparison pair is passed through verbatim (the hook restores it without
 *   recomputation).
 *
 * @param saved - The saved report row (subset returned by GET /api/reports/:id).
 * @param onParseError - Optional callback for the corrupt-layout case (logging).
 */
export function buildReportHydration(
  saved: SavedReportInput,
  onParseError?: (err: unknown) => void,
): ReportHydrationState {
  let parseFailed = false;
  const parsed = safeParseLayout(saved.layoutJson, (err) => {
    parseFailed = true;
    onParseError?.(err);
  });

  const hasPrimaryRange = Boolean(saved.dateRangeStart && saved.dateRangeEnd);

  return {
    pages: migrateLayoutToPages(parsed),
    sectionLabels: extractSectionLabels(parsed),
    activeSections: extractActiveSections(parsed),
    sectionOrder: extractSectionOrder(parsed),
    zoomLevel: extractZoomLevel(parsed),
    hasSavedZoom: hasSavedZoomLevel(parsed),
    format:
      saved.format === "document" || saved.format === "slide-deck"
        ? saved.format
        : "document",
    name: saved.name ?? null,
    dateRange: hasPrimaryRange
      ? {
          start: saved.dateRangeStart as string,
          end: saved.dateRangeEnd as string,
          comparisonStart: saved.comparisonStart,
          comparisonEnd: saved.comparisonEnd,
        }
      : null,
    layoutCorrupt: parseFailed,
  };
}
